import { t } from '../i18n';

/**
 * 种子化 PRNG —— splitmix32。
 *
 * ⚠️ 为什么必须自己写、不能用 `Math.random`：
 * 棋谱只记动作，随机性必须**由 `game/start` 动作携带的种子完全决定**
 * （`architecture.md` §3.8）。用 `Math.random` 的话重放就对不上了。
 *
 * 选 splitmix32 的理由：状态只有一个 32 位整数，**序列化进 state 极简单**，
 * 对 56 张牌的洗牌完全够用。见 `tech-stack.md` §4。
 *
 * ⚠️ 所有位运算都要 `>>> 0` 归一，乘法要 `Math.imul` ——
 * 否则会溢出成浮点，不同引擎上结果就不一样了（`architecture.md` §4）。
 */

/**
 * **一颗种子的 `[0,1)` 随机数发生器。**
 *
 * 项目里所有"看起来是随机的画面"（扰动的相位、裂缝的形状、故障的强度）
 * 都走种子化 PRNG：同一个种子必然给出同一串数，于是"这个观感对不对"
 * 是可复现、可断言的（`architecture.md` §3.8）。
 *
 * ⚠️ 它**不是**给棋谱用的那一份 —— 棋谱的随机性由 `game/start` 携带的种子
 *    完全决定，别把这里的返回值接到规则上去。
 */
export function seededRandom(seed: number): () => number {
  let state: RngState = { seed: seed >>> 0 };
  return () => {
    const step = nextU32(state);
    state = step.state;
    return step.value / 0x100000000;
  };
}

/** splitmix32 的状态 */
export interface RngState {
  /** 32 位无符号整数 */
  readonly seed: number;
}

/** 推进一格，返回 `[0, 2^32)` 的整数与新状态 */
export function nextU32(state: RngState): { readonly value: number; readonly state: RngState } {
  const s = (state.seed + 0x9e3779b9) >>> 0;

  let z = s;
  z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
  z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
  const value = (z ^ (z >>> 15)) >>> 0;

  return { value, state: { seed: s } };
}

/**
 * 无偏的 `[0, n)` 整数。
 *
 * ⚠️ 直接 `nextU32() % n` 在 `n` 不整除 2^32 时是**有偏的**。
 * 对 56 张牌偏差极小，但既然要打"公平洗牌"的招牌，就用拒绝采样做干净。
 *
 * ⚠️ 拒绝采样会**多消耗几格随机数**，所以状态推进的次数不再只由 `n` 决定，
 * 而是由抽到的值决定 —— 这仍然是确定的（同一个种子给同一个结果），
 * 但调用方必须老老实实接住返回的新状态，不能假设"每抽一次只推一格"。
 */
export function nextBelow(
  state: RngState,
  n: number,
): { readonly value: number; readonly state: RngState } {
  if (!Number.isInteger(n) || n <= 0) {
    throw new RangeError(t('core.rng.needPositiveInt', { n }));
  }
  if (n === 1) return { value: 0, state };

  // 落在 [0, limit) 之外的值一律拒绝，因为它们会让取模结果分布不均
  const limit = 0x100000000 - (0x100000000 % n);

  let cursor = state;
  for (;;) {
    const step = nextU32(cursor);
    if (step.value < limit) {
      return { value: step.value % n, state: step.state };
    }
    cursor = step.state;
  }
}

/** 从初始种子建一个 RNG 状态 */
export function seedFrom(seed: number): RngState {
  return { seed: seed >>> 0 };
}
